import { Box, CardMedia, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { COLORS, hideEmptyCss, unit } from '@hcr/ui'
import { isEmpty, isNotEmpty, isNotUndefined, isUndefined } from '@hcr/utils'

import { useBuildingDetailsQuery } from '../../../../hooks'
import type { OwnerHoldingDetailsPathParams } from '../../../../models'
import { Path } from '../../../../models'
import {
  createBuildingDetailsContent,
  createHoldingNameString,
  isHoldingVilla,
  selectNextHoldingWeeks,
  to,
} from '../../../../utils'
import { LayoutNavigationBack, LayoutNavigationBackError404, Metadata, WarningMissingData } from '../../../common'
import { WeeksPreview } from '../common/weeks-preview'
import { useHolding } from '../hooks'

import { OwnerHoldingDetailsLoading } from './OwnerHoldingDetailsLoading'

const BACK_NAVIGATION_PATH = to(Path.Owner)

const buildingDetailsContent = createBuildingDetailsContent()

export const OwnerHoldingDetails: FC = () => {
  const { t } = useTranslation()
  const params = useParams<OwnerHoldingDetailsPathParams>()

  const holding = useHolding({ holdingId: params.holdingId })

  const buildingDetails = useBuildingDetailsQuery(
    { buildingId: String(holding.data?.building_id), isContracted: true },
    { enabled: isNotUndefined(holding.data?.building_id) }
  )

  if (
    isUndefined(params.holdingId) ||
    holding.isError ||
    (holding.isSuccess && isUndefined(holding.data)) ||
    buildingDetails.isError
  ) {
    return <LayoutNavigationBackError404 to={BACK_NAVIGATION_PATH} />
  }

  if (holding.isSuccess && isNotUndefined(holding.data) && buildingDetails.isSuccess) {
    const weeks = selectNextHoldingWeeks(holding.data)
    const isVilla = isHoldingVilla(holding.data)

    return (
      <LayoutNavigationBack to={BACK_NAVIGATION_PATH} arrowVariant='outlined'>
        <Metadata title={t('page-title.owner-holding-details')} />
        <Box bgcolor={COLORS.white} minHeight='100vh'>
          <CardMedia image={buildingDetails.data.imageUrl} sx={{ height: unit(71) }} />
          <Stack
            gap={unit(3)}
            paddingTop={unit(7)}
            paddingRight={unit(9.5)}
            paddingBottom={unit(6)}
            paddingLeft={unit(7.75)}
          >
            <Typography component='h1' variant='headlineL'>
              {isVilla
                ? buildingDetails.data.title
                : t('owner-holding.destination-week', {
                    destinationName: buildingDetails.data.destinationName,
                    weekNumber: (weeks || holding.data.weeks)[0]?.week_number,
                  })}
            </Typography>

            {isVilla ? (
              <Typography variant='bodyL'>
                {createHoldingNameString({ ...holding.data, destinationName: buildingDetails.data.destinationName })}
              </Typography>
            ) : (
              <Stack>
                <Typography variant='bodyL'>{buildingDetails.data.title}</Typography>
                <Typography variant='bodyL'>{createHoldingNameString(holding.data)}</Typography>
              </Stack>
            )}
          </Stack>
          <Stack gap={unit(4)} paddingX={unit(2.75)}>
            {isEmpty(holding.data.weeks) && isEmpty(weeks) && <WarningMissingData />}
            {(isNotEmpty(holding.data.weeks) || isNotEmpty(weeks)) && (
              <WeeksPreview weeks={weeks || holding.data.weeks} holdingId={holding.data.holding_id} isVilla={isVilla} />
            )}
          </Stack>
          <Stack gap={unit(3)} paddingX={unit(3)} paddingBottom={unit(3)} css={hideEmptyCss}>
            {buildingDetails.data.content.map(buildingDetailsContent)}
          </Stack>
        </Box>
      </LayoutNavigationBack>
    )
  }

  return <OwnerHoldingDetailsLoading backNavigationPath={BACK_NAVIGATION_PATH} />
}
